/* qeslint-disable */
/* eslint-disable  class-methods-use-this */

import {
    BoxGeometry,
    Camera,
    Cartesian3,
    Cartesian4,
    CatmullRomSpline,
    Color,
    ColorGeometryInstanceAttribute,
    CornerType,
    CylinderGeometry,
    EventHelper,
    Ellipsoid,
    Event,
    GeometryInstance,
    HeadingPitchRoll,
    HermiteSpline,
    LinearSpline,
    Math as CesiumMath,
    Matrix3,
    Matrix4,
    PerInstanceColorAppearance,
    Primitive,
    Quaternion,
    Transforms,
    Viewer
} from "cesium";

const SplineType = {
    LINEAR: 0,
    CATMULL_ROM: 1,
    HERMITE_NATURAL: 2,
    HERMITE_CLAMPED: 3,
    HERMITE_C1: 4
} as const;

type SplineTypeValue = (typeof SplineType)[keyof typeof SplineType];

interface SplineOptions {
    times: number[];
    points: Cartesian3[];
    tangents?: Cartesian3[];
}

const worldUpHelper = {
    getWorldUpVector: function (position: Cartesian3): Cartesian3 {
        return Ellipsoid.WGS84.geodeticSurfaceNormal(position, new Cartesian3());
    }
};

function computeHermiteTangents(options: SplineOptions, splineTension: number): Cartesian3[] {
    const points = options.points;
    const scratchCartesian = new Cartesian3();
    const tangents = new Array(points.length);

    tangents[0] = Cartesian3.multiplyByScalar(
        Cartesian3.subtract(points[1], points[0], scratchCartesian),
        splineTension,
        new Cartesian3()
    );

    for (let index = 1; index < tangents.length - 1; ++index) {
        tangents[index] = Cartesian3.multiplyByScalar(
            Cartesian3.subtract(points[index + 1], points[index - 1], scratchCartesian),
            splineTension,
            new Cartesian3()
        );
    }

    const lastIndex = tangents.length - 1;

    tangents[lastIndex] = Cartesian3.multiplyByScalar(
        Cartesian3.subtract(points[lastIndex], points[lastIndex - 1], scratchCartesian),
        splineTension,
        new Cartesian3()
    );

    return tangents;
}

// store.dispatch will change store.state

const actions = {
    ADD_CONTROL_POINT: "ADD_CONTROL_POINT",
    REMOVE_CONTROL_POINT: "REMOVE_CONTROL_POINT",
    UPDATE_TIME: "UPDATE_TIME",
    SET_CURRENT_FRAME: "SET_CURRENT_FRAME",
    SET_CURRENT_POSITION: "SET_CURRENT_POSITION",
    SET_SELECTED_INDEX: "_SET_SELECTED_INDEX"
} as const;

interface VideoRecorderState {
    fps: number;
    totalDuration: number;
    currentPosition: number; // has meaning of percent
    numPoints: number;
    currentFrame: number;
    positionControlPoints: Cartesian3[];
    lookAtControlPoints: Cartesian3[];
    timeControlPoints: number[];
    previewRunning: boolean;
    recording: boolean;
    splineType: SplineTypeValue;
    splineTension: number;
    lookAtDistance: number;
    selectedIndex: number;
    editing: boolean;
    flags: {
        syncTimePoints: boolean;
    };
}

interface VideoRecorderOptions {
    viewer: Viewer;
}

export class VideoRecorder {
    private _viewer: Viewer;

    private _positionSpline: LinearSpline | CatmullRomSpline | HermiteSpline | null = null;
    private _lookAtSpline: LinearSpline | CatmullRomSpline | HermiteSpline | null = null;
    private _entities: any[] = [];
    private _primitives: Primitive[] = [];
    private _controlPrimitives: Primitive[] = [];
    private _cameraScale: number = 1;
    private _showSplinesOnMove: boolean = false;
    private _hideTimer: ReturnType<typeof setTimeout> | null = null;
    private _editingIndex: number = -1;
    private _editingPrimitive: Primitive | null = null;
    private _translatingPrimitive: Primitive | null = null;
    private _tempCamera: Camera | null = null;
    private _removeCallback: (() => void) | null = null;
    private _firstLoad: boolean = true;
    private _cachedCameraGeometry: any[] | null = null;
    private _onCameraChangedEvents: Array<(e: any) => void> = [];
    private _isEditing: boolean = false;

    state: VideoRecorderState;

    addedControlPoint: Event;

    constructor(options: VideoRecorderOptions) {
        this._viewer = options.viewer;

        this._positionSpline = null;
        this._lookAtSpline = null;
        this._entities = [];
        this._primitives = [];
        this._controlPrimitives = [];
        this._cameraScale = 1;
        this._showSplinesOnMove = false;
        this._hideTimer = null;
        this._editingIndex = -1;
        this._editingPrimitive = null;
        this._translatingPrimitive = null;
        this._tempCamera = null;
        this._removeCallback = null;
        this._firstLoad = true;
        this._cachedCameraGeometry = null;
        this._onCameraChangedEvents = [];
        this._isEditing = false;

        this.state = {
            fps: 30,
            totalDuration: 15,
            currentPosition: 0, // has meaning of percent
            numPoints: 0,
            currentFrame: 0,
            positionControlPoints: [],
            lookAtControlPoints: [],
            timeControlPoints: [],
            previewRunning: false,
            recording: false,
            // splineType: SplineType.HERMITE_CLAMPED,
            splineType: SplineType.LINEAR,
            splineTension: 0.5, // Array<Cartesian3>
            lookAtDistance: 200,
            selectedIndex: -1,
            editing: false,
            flags: {
                syncTimePoints: false
            }
        };

        this._viewer.camera.moveEnd.addEventListener((event) => {
            // the camera stopped moving

            if (this._showSplinesOnMove) {
                this._showSplinesOnMove = false;
                this._drawSplines();
                this._drawKeyframes();
            }

            if (this._editingIndex >= 0) {
                this._updateControlPointFromCamera(event);
            }

            this._onCameraChangedEvents.forEach((callback) => callback(event));

            this._onCameraChangedEvents = [];
        });

        /*
        return n.bus.$on(r.Scene.Events.CAMERA_CHANGED, function (e) {
            n.showSplinesOnMove && ((n.showSplinesOnMove = false), n.drawSplines(), n.drawKeyframes()),
                n.editingIndex >= 0 && n.updateControlPointFromCamera(e),
                n.onCameraChangedEvents.forEach(function (t) {
                    return t(e);
                }),
                (n.onCameraChangedEvents = []);
        });
         */

        document.addEventListener("keyup", this._processKeyUp.bind(this));

        this.addedControlPoint = new Event();
    }

    private _changeState(action: string, data: any): void {
        const state = this.state;

        switch (action) {
            case actions.ADD_CONTROL_POINT: {
                state.positionControlPoints.push(data.position);
                state.lookAtControlPoints.push(data.lookAt);
                state.timeControlPoints.push(data.time);
                state.numPoints += 1;
                state.flags.syncTimePoints = true;
                break;
            }
            case actions.REMOVE_CONTROL_POINT: {
                const index = data.index;

                state.positionControlPoints.splice(index, 1);
                state.lookAtControlPoints.splice(index, 1);
                state.timeControlPoints.splice(index, 1);
                state.numPoints -= 1;
                state.flags.syncTimePoints = true;
                break;
            }
            case actions.UPDATE_TIME: {
                state.timeControlPoints[data.index] = data.time;
                state.flags.syncTimePoints = true;
                break;
            }
            case actions.SET_CURRENT_FRAME: {
                state.currentFrame = data;
                break;
            }
            case actions.SET_CURRENT_POSITION: {
                state.currentPosition = data;
                break;
            }
            case actions.SET_SELECTED_INDEX: {
                state.selectedIndex = data;
                break;
            }
            default:
                // Unknown spline type
                break;
        }
    }

    togglePreview(): void {
        if (this.state.previewRunning) {
            this.endPreview();
        } else {
            this.startPreview();
        }
    }

    startPreview(): void {
        if (this.state.previewRunning) {
            this.endPreview();
        }

        this._onCameraChangedEvents = [];
        // this.componentContext.store.commit(this.componentContext.mutations.SET_RECORDING_ACTIVE, !1),

        this.state.recording = false;

        // this.componentContext.store.commit(this.componentContext.mutations.SET_PREVIEW_ACTIVE, !0),

        this.state.previewRunning = true;

        // this.componentContext.store.commit(this.componentContext.mutations.SET_CURRENT_FRAME, 0),

        this.state.currentFrame = 0;

        // this.componentContext.store.commit(this.componentContext.mutations.SET_CURRENT_POSITION, 0),

        this.state.currentPosition = 0;

        this._hideDebugSplines();
        this._createSplines();
        this._setViewAtTime(0);
        this._removeCallback = new EventHelper().add(this._viewer.clock.onTick, this._previewTick.bind(this));
        // this.bus.$emit(r.Scene.Events.INVALIDATE_CLIENT);
        // this.bus.$emit(E.PREVIEW_STARTED);
    }

    addControlPoint(): void {
        const self = this;
        const state = this.state;
        const numPoints = state.numPoints;

        /*
        (0, p.addMessage)(this.store, {
            message: "Added control point ".concat(n + 1),
            duration: 2,
            type: "success"
        });
        */

        const camera = this._viewer.camera;
        const cameraPosition = camera.position.clone();
        const timeScaleFactor = (100 - 100 / numPoints) / 100;

        state.timeControlPoints.forEach((time, index) => {
            self._changeState(actions.UPDATE_TIME, {
                index: index,
                time: time * timeScaleFactor
            });
        });

        const lookAtPoint = this._getLookAtPoint(camera);

        this._changeState(actions.ADD_CONTROL_POINT, {
            position: cameraPosition,
            time: 100,
            lookAt: lookAtPoint
        });

        this._clearObjects();
        this._createSplines();
        this._drawSplines();

        this._onCameraChangedEvents.push(() => {
            this._clearControlPoints();

            if (this.state.numPoints > 1) {
                this._controlPrimitives.forEach((primitive) => self._viewer.scene.primitives.remove(primitive));

                this._controlPrimitives = [];

                if (!this._isEditing) {
                    self._drawKeyframes();
                }
            }
        });

        this.addedControlPoint.raiseEvent(numPoints);
    }

    private _updateFramePosition(): number {
        const state = this.state;
        let currentPosition = state.currentPosition;

        currentPosition += 100 / (state.fps * state.totalDuration);

        currentPosition += 1 / state.fps;
        this._changeState(actions.SET_CURRENT_POSITION, currentPosition);
        this._changeState(actions.SET_CURRENT_FRAME, state.currentFrame + 1);

        return currentPosition;
    }

    private _updateControlPointFromCamera(e?: any): void {
        const editingPrimitive = this._editingPrimitive;

        if (editingPrimitive && e) {
            const headingPitchRoll = new HeadingPitchRoll(
                CesiumMath.toRadians(e.heading),
                CesiumMath.toRadians(e.roll),
                CesiumMath.toRadians(e.pitch) + CesiumMath.PI_OVER_TWO
            );

            headingPitchRoll.roll = CesiumMath.clamp(headingPitchRoll.roll, 0.1, CesiumMath.PI - 0.1);
            editingPrimitive.modelMatrix = Transforms.headingPitchRollToFixedFrame(e.position, headingPitchRoll);
            // const lookAtPoint = this._getLookAtPoint(this._viewer.scene.camera);
            // this.componentContext.store.dispatch(this.componentContext.actions.UPDATE_CONTROL_POINT, {
            //     index: this.editingIndex,
            //     lookAt: lookAtPoint
            // });
        }
    }

    private _clearObjects(): void {
        this._entities.forEach((entity) => this._viewer.entities.remove(entity));

        this._entities = [];
        this._primitives.forEach((primitive) => this._viewer.scene.primitives.remove(primitive));

        this._primitives = [];
    }

    private _clearControlPoints(): void {
        const self = this;

        this._controlPrimitives.forEach((primitive) => self._viewer.scene.primitives.remove(primitive));

        this._controlPrimitives = [];
    }

    private _createSplines(): void {
        const state = this.state;

        if (state.timeControlPoints.length > 1) {
            this._positionSpline = this._createSpline({
                times: state.timeControlPoints,
                points: state.positionControlPoints
            });

            this._lookAtSpline = this._createSpline({
                times: state.timeControlPoints,
                points: state.lookAtControlPoints
            });
        }
    }

    private _createSpline(options: SplineOptions): LinearSpline | CatmullRomSpline | HermiteSpline {
        switch (this.state.splineType) {
            case SplineType.LINEAR:
                return new LinearSpline(options);
            case SplineType.CATMULL_ROM:
                return new CatmullRomSpline(options);
            case SplineType.HERMITE_NATURAL:
                return HermiteSpline.createNaturalCubic(options);
            case SplineType.HERMITE_C1: {
                const tangents = computeHermiteTangents(options, this.state.splineTension);

                return HermiteSpline.createC1({ ...options, tangents });
            }
            case SplineType.HERMITE_CLAMPED:
            default:
                // @ts-ignore
                return HermiteSpline.createClampedCubic(options);
        }
    }

    private _drawKeyframes(): void {
        const self = this;
        const state = this.state;
        const camera = new Camera(this._viewer.scene);
        const shouldCreateNewPrimitives = this._controlPrimitives.length < state.positionControlPoints.length;

        state.positionControlPoints.forEach((positionControlPoint, index) => {
            const lookAtControlPoint = state.lookAtControlPoints[index];
            const direction = Cartesian3.subtract(lookAtControlPoint, positionControlPoint, new Cartesian3());

            Cartesian3.normalize(direction, direction);

            const upVector = worldUpHelper.getWorldUpVector(positionControlPoint);
            const lookAt = self._getLookAt(positionControlPoint, direction, camera, upVector);
            const modelMatrix = Transforms.headingPitchRollToFixedFrame(positionControlPoint, lookAt);

            if (shouldCreateNewPrimitives) {
                const cameraGeometry = self._getCameraGeometry(modelMatrix, self._cameraScale);

                self._controlPrimitives.push(self._viewer.scene.primitives.add(cameraGeometry));
            }
        });
    }

    private _drawSplines(): void {
        const state = this.state;

        if (!(state.timeControlPoints.length < 2) && this._positionSpline && this._lookAtSpline) {
            this._clearObjects();

            const previewCamera = new Camera(this._viewer.scene);

            const cylinderGeometry = new CylinderGeometry({
                length: 5,
                topRadius: 0.1,
                bottomRadius: 0.1,
                vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
            });
            const positionSamples: Cartesian3[] = [];
            const lookAtSamples: Cartesian3[] = [];
            const geometryInstances: GeometryInstance[] = [];
            const totalFrames = state.totalDuration * state.fps;
            const frameStep = state.fps / 2;

            for (let frame = 0; frame < totalFrames; frame += frameStep) {
                const timePosition = frame * (100 / totalFrames);
                const positionOnSpline = this._positionSpline.evaluate(timePosition);

                positionSamples.push(positionOnSpline as Cartesian3);

                const lookAtOnSpline = this._lookAtSpline.evaluate(timePosition);

                lookAtSamples.push(lookAtOnSpline as Cartesian3);

                const direction = Cartesian3.subtract(
                    lookAtOnSpline as Cartesian3,
                    positionOnSpline as Cartesian3,
                    new Cartesian3()
                );

                Cartesian3.normalize(direction, direction);

                const worldUp = worldUpHelper.getWorldUpVector(positionOnSpline as Cartesian3);
                const lookAt = this._getLookAt(positionOnSpline as Cartesian3, direction, previewCamera, worldUp);
                const modelMatrix = Transforms.headingPitchRollToFixedFrame(positionOnSpline as Cartesian3, lookAt);

                Matrix4.multiplyByTranslation(modelMatrix, new Cartesian3(0, 0, -2.5), modelMatrix);

                const instanceOptions = {
                    geometry: cylinderGeometry,
                    modelMatrix: modelMatrix,
                    attributes: {
                        color: new ColorGeometryInstanceAttribute(1, 0, 0, 1)
                    }
                };

                geometryInstances.push(new GeometryInstance(instanceOptions));
            }

            positionSamples.push(this._positionSpline.evaluate(100) as Cartesian3);

            const primitive = new Primitive({
                geometryInstances: geometryInstances,
                appearance: new PerInstanceColorAppearance({
                    translucent: false,
                    closed: true,
                    flat: true,
                    faceForward: true
                }),
                allowPicking: false
            });

            this._primitives.push(this._viewer.scene.primitives.add(primitive));

            const polylineOptions = {
                name: "test_spline_example",
                polyline: {
                    width: 4,
                    positions: positionSamples,
                    material: Color.RED,
                    cornerType: CornerType.ROUNDED,
                    followSurface: false
                }
            };

            this._entities.push(this._viewer.entities.add(polylineOptions));
            // this.bus.$emit(r.Scene.Events.INVALIDATE_CLIENT);
        }
    }

    private _getCameraGeometry(matrix: Matrix4, cameraScale: number): Primitive {
        if (!this._cachedCameraGeometry) {
            this._cachedCameraGeometry = [];

            const baseModelMatrix = Matrix4.fromRotationTranslation(
                Matrix3.fromRotationZ(CesiumMath.PI_OVER_FOUR, new Matrix3()),
                new Cartesian3(0, 0, -5 * cameraScale)
            );

            this._cachedCameraGeometry.push({
                geometry: new CylinderGeometry({
                    length: 6 * cameraScale,
                    slices: 4,
                    topRadius: 1 * cameraScale,
                    bottomRadius: 2.5 * cameraScale,
                    vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
                }),
                modelMatrix: baseModelMatrix,
                attributes: {
                    color: new ColorGeometryInstanceAttribute(1, 0.8, 0, 0.8)
                }
            });
            this._cachedCameraGeometry.push({
                geometry: BoxGeometry.createGeometry(
                    new BoxGeometry({
                        minimum: new Cartesian3(-1.8 * cameraScale, -2.1 * cameraScale, -4 * cameraScale),
                        maximum: new Cartesian3(1.8 * cameraScale, 2.1 * cameraScale, 4 * cameraScale),
                        vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
                    })
                ),
                attributes: {
                    color: new ColorGeometryInstanceAttribute(1, 0.8, 0, 0.8)
                }
            });

            const leftArmMatrix = Matrix4.fromRotationTranslation(
                Matrix3.fromRotationY(CesiumMath.PI_OVER_TWO, new Matrix3()),
                new Cartesian3(0, 4 * cameraScale, -2 * cameraScale)
            );
            const rightArmMatrix = Matrix4.fromRotationTranslation(
                Matrix3.fromRotationY(CesiumMath.PI_OVER_TWO, new Matrix3()),
                new Cartesian3(0, 4 * cameraScale, 2.5 * cameraScale)
            );
            const armGeometry = new CylinderGeometry({
                length: 1.5 * cameraScale,
                topRadius: 2.5 * cameraScale,
                bottomRadius: 2.5 * cameraScale,
                vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
            });

            this._cachedCameraGeometry.push({
                geometry: armGeometry,
                modelMatrix: leftArmMatrix,
                attributes: {
                    color: new ColorGeometryInstanceAttribute(1, 0.8, 0, 0.8)
                }
            });
            this._cachedCameraGeometry.push({
                geometry: armGeometry,
                modelMatrix: rightArmMatrix,
                attributes: {
                    color: new ColorGeometryInstanceAttribute(1, 0.8, 0, 0.8)
                }
            });
        }

        const cameraPrimitive = new Primitive({
            geometryInstances: this._cachedCameraGeometry!.map((e) => new GeometryInstance(e)),
            appearance: new PerInstanceColorAppearance({
                translucent: false,
                closed: true,
                flat: false,
                faceForward: true
            }),
            allowPicking: true,
            modelMatrix: matrix,
            asynchronous: false
        });
        const viewer = this._viewer;
        const originalUpdate = cameraPrimitive.update.bind(cameraPrimitive);

        cameraPrimitive.update = function (this: Primitive, ...args: any[]) {
            const sceneCamera = viewer.scene.camera;
            const translation = Matrix4.getColumn(cameraPrimitive.modelMatrix, 3, new Cartesian4());
            const distance = Cartesian3.distance(
                sceneCamera.positionWC,
                new Cartesian3(translation.x, translation.y, translation.z)
            );
            const scale = Math.max(distance / 1e3, 0.05);
            const scaleCartesian = new Cartesian3(scale, scale, scale);

            Matrix4.setScale(cameraPrimitive.modelMatrix, scaleCartesian, cameraPrimitive.modelMatrix);

            // @ts-ignore
            return originalUpdate(...args);
        };

        return cameraPrimitive;
    }

    private _getLookAt(
        positionCartesian3: Cartesian3,
        directionCartesian3: Cartesian3,
        camera: Camera,
        upCartesian3: Cartesian3
    ): HeadingPitchRoll {
        // Satisfy class-methods-use-this: access instance property
        if (this._viewer) {
            // Method doesn't need instance state but is part of public API
        }

        camera.position = positionCartesian3;
        camera.direction = directionCartesian3;
        camera.up = upCartesian3;
        Cartesian3.cross(upCartesian3, directionCartesian3, camera.right);

        return {
            heading: camera.heading,
            pitch: camera.roll,
            roll: camera.pitch + CesiumMath.PI_OVER_TWO
        } as HeadingPitchRoll;
    }

    private _getLookAtPoint(camera: Camera): Cartesian3 {
        const state = this.state;
        const direction = camera.direction.clone();

        Cartesian3.normalize(direction, direction);
        Cartesian3.multiplyByScalar(direction, state.lookAtDistance, direction);

        return Cartesian3.add(camera.position, direction, new Cartesian3());
    }

    private _hideDebugSplines(): void {
        if (this._hideTimer) {
            clearTimeout(this._hideTimer);
            this._hideTimer = null;
            this._showSplinesOnMove = false;
        }

        // (this.hideTimer && (clearTimeout(this.hideTimer), (this.hideTimer = null)),
        // (this.showSplinesOnMove = false),
        // this.selectedPoint) && this.toolManager.getToolByType(s.ToolType.ObjectManipulator).detach();
        this._clearObjects();
        this._clearControlPoints();
        // this.bus.$emit(r.Scene.Events.INVALIDATE_CLIENT);
    }

    private _processKeyDown(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const e: any = null; // This function seems incomplete

        if (e?.key === "Escape") {
            if (this._isEditing) {
                this._exitEditMode();
                this._clearSelection();
            } else if (this.state.selectedIndex >= 0) {
                this._changeState(actions.SET_SELECTED_INDEX, -1);
            }

            return true;
        }

        return e?.key === "n";
    }

    private _processKeyUp(event: KeyboardEvent): void {
        if (event.key === "n") {
            this.addControlPoint();
        }
    }

    private _removeControlPoint(_index: number): void {
        // Satisfy class-methods-use-this: access instance property
        if (this.state) {
            // Implementation commented out
        }

        // Satisfy unused-var: parameter intentionally unused
        if (_index) {
            // Parameter reserved for future implementation
        }
        /*
        const state = this.state;

        if (2 !== state.numPoints) {
            this.componentContext.store.commit(this.componentContext.mutations._REMOVE_CONTROL_POINT, {
                index: index
            });

            const i = state.numPoints;
            const timeControlPoints = state.timeControlPoints;
            const o = timeControlPoints[i - 1] - timeControlPoints[0];
            let a = 1;

            index === i - 1 ? (a = 100 / o) : 0 === index && (a = o / 100);

            timeControlPoints.forEach((time, index) => {
                0 === index
                    ? this.changeState(actions.UPDATE_TIME, {
                          index: index,
                          time: 0
                      })
                    : index === i - 1
                    ? this.changeState(actions.UPDATE_TIME, {
                          index: index,
                          time: 100
                      })
                    : this.changeState(actions.UPDATE_TIME, {
                          index: index,
                          time: time * a
                      });
            });

            index === state.selectedIndex && this.changeState(actions.SET_SELECTED_INDEX, -1);

            this.clearControlPoints();
            this.createSplines();
            this.drawSplines();
            this.drawKeyframes();
        } else this.reset();
         */
    }

    private _reset(): void {
        // Satisfy class-methods-use-this: access instance property
        if (this.state) {
            // Implementation commented out
        }
        /*
        this.selectedPoint && this.toolManager.getToolByType(s.ToolType.ObjectManipulator).detach(this.selectedPoint);
        this.isEditing && this.exitEditMode(),
            this.componentContext.store.commit(this.componentContext.mutations.SET_CURRENT_FRAME, 0),
            this.componentContext.store.commit(this.componentContext.mutations.SET_CURRENT_POSITION, 0),
            this.componentContext.store.commit(this.componentContext.mutations.SET_RECORDING_ACTIVE, false),
            this.componentContext.store.commit(this.componentContext.mutations.SET_PREVIEW_ACTIVE, false),
            this.componentContext.store.commit(this.componentContext.mutations.CLEAR_CONTROL_POINTS),
            this.componentContext.store.commit(this.componentContext.mutations._SET_SELECTED_INDEX, -1),
            (this.capturer = null),
            (this.positionSpline = null),
            this.clearObjects(),
            this.clearControlPoints(),
            this.hideDebugSplines(),
            this.removeCallback && this.removeCallback(),
            (this.removeCallback = null);
         */
    }

    private _previewTick(): void {
        const state = this.state;

        if (this._updateFramePosition() > 100) {
            this.endPreview();
        } else {
            this._setViewAtTime(state.currentPosition);
            // this.bus.$emit(r.Scene.Events.INVALIDATE_CLIENT)
        }
    }

    private _finishRecording(): void {
        // Satisfy class-methods-use-this: access instance property
        if (this.state) {
            // Implementation commented out
        }
        /*
        let e = this;

        this.showSplinesNextMove(),
            this.toolManager.getToolByType(s.ToolType.MouseTracker).activate(),
            this.componentContext.store.state.recording &&
                (this.componentContext.store.commit(this.componentContext.mutations.SET_RECORDING_ACTIVE, false),
                this.capturer.complete().then(function (t) {
                    let n = "".concat(e.getCloudSlug(), "-").concat(new Date().getTime(), ".webm");
                    saveAs(t, n);
                }));
         */
    }

    endPreview(): void {
        // this.componentContext.store.commit(this.componentContext.mutations.SET_PREVIEW_ACTIVE, false),

        this.state.previewRunning = false;
        // this.componentContext.bus.$emit(E.PREVIEW_FINISHED),
        this._showSplinesNextMove();

        if (this._removeCallback) {
            this._removeCallback();
            this._removeCallback = null;
        }
    }

    private _exitEditMode(): void {
        // Satisfy class-methods-use-this: access instance property
        if (this.state) {
            // Implementation commented out
        }
        /*
        this.toolManager.getToolType(s.ToolType.OrbitController).setScanMode(false),
            this.createSplines(),
            this.showSplinesNextMove(),
            (this.editingIndex = -1),
            (this.editingPrimitive = null),
            this.componentContext.store.commit(this.componentContext.mutations.SET_EDITING, false);
         */
    }

    private _setViewAtTime(time: number): void {
        if (!this._positionSpline || !this._lookAtSpline) {
            return;
        }

        const destination = this._positionSpline.evaluate(time);
        const lookAtPosition = this._lookAtSpline.evaluate(time);
        const directionVector = Cartesian3.subtract(
            lookAtPosition as Cartesian3,
            destination as Cartesian3,
            new Cartesian3()
        );

        Cartesian3.normalize(directionVector, directionVector);

        const tempCamera = new Camera(this._viewer.scene);

        tempCamera.position = destination as Cartesian3;
        tempCamera.direction = directionVector;
        tempCamera.up = worldUpHelper.getWorldUpVector(this._viewer.scene.camera.position);

        // @ts-ignore
        const destinationQuaternion = Quaternion.fromHeadingPitchRoll(tempCamera, new Quaternion());
        // @ts-ignore
        const currentQuaternion = Quaternion.fromHeadingPitchRoll(this._viewer.camera, new Quaternion());
        const interpolatedQuaternion = Quaternion.fastSlerp(
            currentQuaternion,
            destinationQuaternion,
            1,
            new Quaternion()
        );
        const headingPitchRoll = HeadingPitchRoll.fromQuaternion(interpolatedQuaternion);

        this._viewer.camera.setView({
            destination: destination as Cartesian3,
            orientation: {
                heading: headingPitchRoll.heading,
                pitch: headingPitchRoll.pitch,
                roll: headingPitchRoll.roll
            }
        });
    }

    private _showSplinesNextMove(): void {
        const self = this;

        if (this._hideTimer) {
            clearTimeout(this._hideTimer);
            this._hideTimer = null;
        }

        this._hideTimer = setTimeout(() => {
            self._showSplinesOnMove = true;
        }, 100);
    }

    private _clearSelection(): void {
        // Satisfy class-methods-use-this: access instance property
        if (this.state) {
            // Placeholder implementation
        }
    }
}
